import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { LoadingState } from '../shared/ui/query-state.tsx'
import { AppShell } from './app-shell.tsx'
import { AdminLayout } from './admin-layout.tsx'
import { AdminSessionBoundary } from '../modules/admin/admin-session-boundary.tsx'
import { AdminLegacyCollectionRedirect } from '../pages/admin-legacy-collection-redirect.tsx'

const WorldPage = lazy(() =>
  import('../pages/world-page.tsx').then((module) => ({ default: module.WorldPage })),
)
const MomentPage = lazy(() =>
  import('../pages/moment-page.tsx').then((module) => ({ default: module.MomentPage })),
)
const EditionPage = lazy(() =>
  import('../pages/edition-page.tsx').then((module) => ({ default: module.EditionPage })),
)
const CheckoutPage = lazy(() =>
  import('../pages/checkout-page.tsx').then((module) => ({ default: module.CheckoutPage })),
)
const OrderPage = lazy(() =>
  import('../pages/order-page.tsx').then((module) => ({ default: module.OrderPage })),
)
const AccountPage = lazy(() =>
  import('../pages/account-page.tsx').then((module) => ({ default: module.AccountPage })),
)
const ReaderPage = lazy(() =>
  import('../pages/reader-page.tsx').then((module) => ({ default: module.ReaderPage })),
)
const AudioPage = lazy(() =>
  import('../pages/audio-page.tsx').then((module) => ({ default: module.AudioPage })),
)
const VideoPage = lazy(() =>
  import('../pages/video-page.tsx').then((module) => ({ default: module.VideoPage })),
)
const InteractivePage = lazy(() =>
  import('../pages/interactive-page.tsx').then((module) => ({ default: module.InteractivePage })),
)
const MagicLinkPage = lazy(() =>
  import('../pages/magic-link-page.tsx').then((module) => ({ default: module.MagicLinkPage })),
)
const AdminLoginPage = lazy(() =>
  import('../pages/admin-login-page.tsx').then((module) => ({ default: module.AdminLoginPage })),
)
const AdminOperationsPage = lazy(() =>
  import('../pages/admin-operations-page.tsx').then((module) => ({
    default: module.AdminOperationsPage,
  })),
)
const AdminMigrationPage = lazy(() =>
  import('../pages/admin-migration-page.tsx').then((module) => ({
    default: module.AdminMigrationPage,
  })),
)
const AdminReliabilityPage = lazy(() =>
  import('../pages/admin-reliability-page.tsx').then((module) => ({
    default: module.AdminReliabilityPage,
  })),
)
const AdminOrderDetailPage = lazy(() =>
  import('../pages/admin-order-detail-page.tsx').then((module) => ({
    default: module.AdminOrderDetailPage,
  })),
)
const AdminMomentsPage = lazy(() =>
  import('../pages/admin-moments-page.tsx').then((module) => ({
    default: module.AdminMomentsPage,
  })),
)
const AdminMomentCreatePage = lazy(() =>
  import('../pages/admin-moment-create-page.tsx').then((module) => ({
    default: module.AdminMomentCreatePage,
  })),
)
const AdminMomentEditPage = lazy(() =>
  import('../pages/admin-moment-edit-page.tsx').then((module) => ({
    default: module.AdminMomentEditPage,
  })),
)
const AdminEditionsPage = lazy(() =>
  import('../pages/admin-editions-page.tsx').then((module) => ({
    default: module.AdminEditionsPage,
  })),
)
const AdminEditionCreatePage = lazy(() =>
  import('../pages/admin-edition-create-page.tsx').then((module) => ({
    default: module.AdminEditionCreatePage,
  })),
)
const AdminEditionEditPage = lazy(() =>
  import('../pages/admin-edition-edit-page.tsx').then((module) => ({
    default: module.AdminEditionEditPage,
  })),
)
const AdminOffersPage = lazy(() =>
  import('../pages/admin-offers-page.tsx').then((module) => ({
    default: module.AdminOffersPage,
  })),
)
const AdminCouponsPage = lazy(() =>
  import('../pages/admin-coupons-page.tsx').then((module) => ({
    default: module.AdminCouponsPage,
  })),
)
const AdminSitePage = lazy(() =>
  import('../pages/admin-site-page.tsx').then((module) => ({ default: module.AdminSitePage })),
)
const AdminNewsletterPage = lazy(() =>
  import('../pages/admin-newsletter-page.tsx').then((module) => ({
    default: module.AdminNewsletterPage,
  })),
)
const InstitutionalPage = lazy(() =>
  import('../pages/institutional-page.tsx').then((module) => ({
    default: module.InstitutionalPage,
  })),
)
const NewsletterActionPage = lazy(() =>
  import('../pages/newsletter-action-page.tsx').then((module) => ({
    default: module.NewsletterActionPage,
  })),
)
const NotFoundPage = lazy(() =>
  import('../pages/not-found-page.tsx').then((module) => ({ default: module.NotFoundPage })),
)

function suspended(element: React.ReactNode) {
  return <Suspense fallback={<LoadingState />}>{element}</Suspense>
}

export const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: '/', element: suspended(<WorldPage />) },
      { path: '/momentos/:momentSlug', element: suspended(<MomentPage />) },
      { path: '/momentos/:momentSlug/edicoes/:editionSlug', element: suspended(<EditionPage />) },
      { path: '/checkout', element: suspended(<CheckoutPage />) },
      { path: '/pedido/:id', element: suspended(<OrderPage />) },
      { path: '/minha-conta/edicoes', element: suspended(<AccountPage />) },
      { path: '/minha-conta/edicoes/:editionId/leitor', element: suspended(<ReaderPage />) },
      { path: '/minha-conta/edicoes/:editionId/audio', element: suspended(<AudioPage />) },
      { path: '/minha-conta/edicoes/:editionId/video', element: suspended(<VideoPage />) },
      {
        path: '/minha-conta/edicoes/:editionId/interativo',
        element: suspended(<InteractivePage />),
      },
      { path: '/entrar', element: suspended(<MagicLinkPage />) },
      { path: '/sobre', element: suspended(<InstitutionalPage pageKey="about" />) },
      { path: '/privacidade', element: suspended(<InstitutionalPage pageKey="privacy" />) },
      { path: '/termos', element: suspended(<InstitutionalPage pageKey="terms" />) },
      {
        path: '/newsletter/confirmar',
        element: suspended(<NewsletterActionPage action="confirmation" />),
      },
      {
        path: '/newsletter/cancelar',
        element: suspended(<NewsletterActionPage action="cancellation" />),
      },
      { path: '*', element: suspended(<NotFoundPage />) },
    ],
  },
  {
    path: '/admin/entrar',
    element: (
      <main className="admin-login-shell" id="admin-login-content">
        {suspended(<AdminLoginPage />)}
      </main>
    ),
  },
  {
    path: '/admin',
    element: <AdminSessionBoundary />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="momentos" replace /> },
          { path: 'momentos', element: suspended(<AdminMomentsPage />) },
          { path: 'momentos/novo', element: suspended(<AdminMomentCreatePage />) },
          { path: 'momentos/:momentId/editar', element: suspended(<AdminMomentEditPage />) },
          { path: 'edicoes', element: suspended(<AdminEditionsPage />) },
          { path: 'edicoes/nova', element: suspended(<AdminEditionCreatePage />) },
          { path: 'edicoes/:editionId/editar', element: suspended(<AdminEditionEditPage />) },
          { path: 'acervo', element: <AdminLegacyCollectionRedirect /> },
          { path: 'ofertas', element: suspended(<AdminOffersPage />) },
          { path: 'cupons', element: suspended(<AdminCouponsPage />) },
          { path: 'site', element: suspended(<AdminSitePage />) },
          { path: 'newsletter', element: suspended(<AdminNewsletterPage />) },
          { path: 'operacao', element: suspended(<AdminOperationsPage />) },
          { path: 'migracao', element: suspended(<AdminMigrationPage />) },
          { path: 'saude', element: suspended(<AdminReliabilityPage />) },
          {
            path: 'operacao/pedidos/:id',
            element: suspended(<AdminOrderDetailPage />),
          },
          { path: '*', element: <Navigate to="momentos" replace /> },
        ],
      },
    ],
  },
])
